<template>
  <div class="tab-pane fade show active">
    <LoadingIndicatorComponent
      v-if="loading"
      message="Loading weekly price statistics..."
    />
    <div v-else class="card">
      <div
        class="card-header d-flex justify-content-between align-items-center"
      >
        <div>
          <h5 class="mb-0">Weekly Price Statistics</h5>
          <small class="text-muted">
            Weekly min/avg/max of bid, ask and mid prices per product and
            vintage
          </small>
        </div>
        <div class="d-flex align-items-center gap-2">
          <label class="form-label mb-0 text-nowrap" for="weeks-select">
            Weeks
          </label>
          <select
            id="weeks-select"
            v-model.number="weeks"
            class="form-select form-select-sm"
            @change="fetchWeeklyStats"
          >
            <option :value="4">4</option>
            <option :value="12">12</option>
            <option :value="26">26</option>
            <option :value="52">52</option>
          </select>
        </div>
      </div>
      <div class="card-body">
        <div v-if="error" class="alert alert-danger">{{ error }}</div>
        <div v-else-if="stats.length === 0" class="alert alert-info">
          No weekly price data available yet.
        </div>
        <div v-else>
          <div v-for="product in productGroups" :key="product.key" class="mb-4">
            <h6 class="mb-2">
              {{ product.label }}
              <small class="text-muted">({{ product.name }})</small>
            </h6>
            <div class="table-responsive">
              <table class="table table-sm table-striped align-middle">
                <thead>
                  <tr>
                    <th rowspan="2" class="align-middle">Week</th>
                    <th rowspan="2" class="align-middle">Vintage</th>
                    <th rowspan="2" class="align-middle text-end">Days</th>
                    <th colspan="3" class="text-center">Bid</th>
                    <th colspan="3" class="text-center">Ask</th>
                    <th colspan="3" class="text-center">Mid</th>
                  </tr>
                  <tr>
                    <th class="text-end">Min</th>
                    <th class="text-end">Avg</th>
                    <th class="text-end">Max</th>
                    <th class="text-end">Min</th>
                    <th class="text-end">Avg</th>
                    <th class="text-end">Max</th>
                    <th class="text-end">Min</th>
                    <th class="text-end">Avg</th>
                    <th class="text-end">Max</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in product.rows"
                    :key="`${row.week_start}-${row.vintage}`"
                  >
                    <td class="text-nowrap">{{ row.week_start }}</td>
                    <td>{{ row.vintage ?? "-" }}</td>
                    <td class="text-end">{{ row.sample_count }}</td>
                    <td class="text-end">
                      {{ formatPriceOrDash(row.bid_min) }}
                    </td>
                    <td class="text-end">
                      {{ formatPriceOrDash(row.bid_avg) }}
                    </td>
                    <td class="text-end">
                      {{ formatPriceOrDash(row.bid_max) }}
                    </td>
                    <td class="text-end">
                      {{ formatPriceOrDash(row.ask_min) }}
                    </td>
                    <td class="text-end">
                      {{ formatPriceOrDash(row.ask_avg) }}
                    </td>
                    <td class="text-end">
                      {{ formatPriceOrDash(row.ask_max) }}
                    </td>
                    <td class="text-end">
                      {{ formatPriceOrDash(row.mid_min) }}
                    </td>
                    <td class="text-end">
                      {{ formatPriceOrDash(row.mid_avg) }}
                    </td>
                    <td class="text-end">
                      {{ formatPriceOrDash(row.mid_max) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { computed, onMounted, ref } from "vue";

import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { formatPriceOrDash } from "@/market_insights/admin/eacPricingFormat";

interface WeeklyPriceStats {
  name: string;
  source: string;
  label: string;
  vintage: string | null;
  week_start: string;
  week_end: string;
  sample_count: number;
  bid_min: number | null;
  bid_max: number | null;
  bid_avg: number | null;
  ask_min: number | null;
  ask_max: number | null;
  ask_avg: number | null;
  mid_min: number | null;
  mid_max: number | null;
  mid_avg: number | null;
}

interface ProductGroup {
  key: string;
  name: string;
  source: string;
  label: string;
  rows: WeeklyPriceStats[];
}

const { globalErrorHandler } = useGlobalHandlers();

const stats = ref<WeeklyPriceStats[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const weeks = ref(12);

const productGroups = computed<ProductGroup[]>(() => {
  const groups = new Map<string, ProductGroup>();

  stats.value.forEach((row) => {
    const key = `${row.name}|||${row.source}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        name: row.name,
        source: row.source,
        label: row.label,
        rows: [],
      };
      groups.set(key, group);
    }
    group.rows.push(row);
  });

  return Array.from(groups.values());
});

// Guards against an older, slower response overwriting the latest selection
let latestRequestId = 0;

const fetchWeeklyStats = async () => {
  const requestId = ++latestRequestId;
  loading.value = true;
  error.value = null;
  try {
    const response = await goSolidApi.get(
      `/api/admin/eac-pricing/weekly-stats?weeks=${weeks.value}`
    );
    if (requestId === latestRequestId) {
      stats.value = response.data.data;
    }
  } catch (err: any) {
    if (requestId === latestRequestId) {
      error.value = "Failed to fetch weekly price statistics";
    }
    globalErrorHandler(err);
  } finally {
    if (requestId === latestRequestId) {
      loading.value = false;
    }
  }
};

onMounted(async () => {
  await fetchWeeklyStats();
});
</script>

<style lang="scss" scoped>
#weeks-select {
  width: 5rem;
}
</style>
